import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MdDialogRef } from '@angular/material';
import { Observable } from 'rxjs/Observable';

import { BadPressService } from 'app/entities/monitoring/actual/badpress/bad-press.service';
import { PoliticallyExposedService } from 'app/entities/monitoring/actual/politicallyexposed/politically-exposed.service';
import { SanctionEntity, SanctionMatchRecord } from 'app/entities/monitoring/actual/sanction-matches.model';
import { SanctionService } from 'app/entities/monitoring/actual/sanction/sanction.service';
import { PepMatchRecord } from 'app/entities/monitoring/actual/pep-match.model';
import { BadPressMatchRecord } from 'app/entities/monitoring/actual/badpress-match.model';
import { MessageService } from 'app/services/messages.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { ApplicationTypeConstants, WarmUpService } from 'app/services/warmup/warmup.service';

export type HitDetailType = 'Sanction' | 'Pep' | 'Badpress';

export interface HitDetailRow {
  type: HitDetailType;
  listName: string;
  hitTokens: string;
  source?: string;
  subTitle?: string;
  hitDate?: any;
  matchResolution?: any;
  falseMatchReason?: any;
  comments?: string;
  relationEntityId?: number;
  listType: 'open' | 'confirmed' | 'history';
  raw: any;
}

@Component({
  selector: 'app-hit-detail-dialog',
  templateUrl: './hit-detail-dialog.component.html',
  styleUrls: ['./hit-detail-dialog.component.css'],
  providers: [SanctionService, BadPressService]
})
export class HitDetailDialogComponent implements OnInit {
  row: HitDetailRow;
  entityId: number;

  loadingDetail = false;
  submitting = false;

  resolveForm: FormGroup;
  matchResolutions: ApplicationValue[];
  falseMatchReasons: ApplicationValue[];
  selectedMatchResolution: string;

  formErrors = {
    matchResolution: ''
  };
  validationMessages = {
    matchResolution: {
      required: 'Required field value.'
    }
  };

  constructor(
    public dialogRef: MdDialogRef<HitDetailDialogComponent>,
    private formBuilder: FormBuilder,
    private warmUpService: WarmUpService,
    private cdr: ChangeDetectorRef,
    private sanctionService: SanctionService,
    private politicallyExposedService: PoliticallyExposedService,
    private badPressService: BadPressService,
    private snackBarService: SnackBarService
  ) {}

  ngOnInit() {
    this.buildResolveForm();
    this.setUpMatchResolutions(
      this.warmUpService.getApplicationValue(
        this.row && this.row.type === 'Badpress'
          ? ApplicationTypeConstants.PANAMA_PAPERS_MATCH_RESOLUTION
          : ApplicationTypeConstants.SANCTION_MATCH_RESOLUTION));
    this.setUpFalseMatchReasons(
      this.warmUpService.getApplicationValue(ApplicationTypeConstants.FALSE_MATCH_REASONS));
    this.loadDetailIfNeeded();
  }

  get isOpen(): boolean {
    return !!this.row && this.row.listType === 'open';
  }

  get listDisplayName(): string {
    if (!this.row) {
      return '';
    }
    // Adverse media: prefer article headline over entity / matchedName in JSON.
    if (this.row.type === 'Badpress' && this.isAdverseMediaNews) {
      const headline = this.adverseHeadline;
      if (headline) {
        return headline;
      }
    }
    const fromJson = this.extractListDisplayName(this.detailJsonRaw);
    if (fromJson) {
      return fromJson;
    }
    const tokens = (this.row.hitTokens || this.extractMatchedTokens(this.detailJsonRaw) || '').trim();
    const fromRow = (this.row.listName || this.entityNameFromRaw || '').trim();
    if (fromRow && (!tokens || fromRow.toLowerCase() !== tokens.toLowerCase())) {
      return fromRow;
    }
    return fromRow || '';
  }

  get hitTokensDisplay(): string {
    if (!this.row) {
      return '—';
    }
    const fromRow = (this.row.hitTokens || '').trim();
    if (fromRow) {
      return fromRow;
    }
    const fromJson = this.extractMatchedTokens(this.detailJsonRaw);
    if (fromJson) {
      return fromJson;
    }
    // Legacy: tokens stored as global/list name while JSON has the real list name.
    const listFromJson = this.extractListDisplayName(this.detailJsonRaw);
    const entityName = (this.entityNameFromRaw || '').trim();
    if (entityName && listFromJson && entityName.toLowerCase() !== listFromJson.toLowerCase()) {
      return entityName;
    }
    return '—';
  }

  get entityNameFromRaw(): string {
    if (!this.row || !this.row.raw) {
      return '';
    }
    if (this.row.type === 'Sanction' && this.row.raw.sanctionEntity) {
      return this.row.raw.sanctionEntity.globalName
        || `${this.row.raw.sanctionEntity.firstName || ''} ${this.row.raw.sanctionEntity.lastName || ''}`.trim();
    }
    if (this.row.type === 'Pep' && this.row.raw.pepEntity) {
      return this.row.raw.pepEntity.globalName
        || `${this.row.raw.pepEntity.firstName || ''} ${this.row.raw.pepEntity.lastName || ''}`.trim();
    }
    if (this.row.type === 'Badpress' && this.row.raw.badPressEntity) {
      const bp = this.row.raw.badPressEntity;
      return bp.title || bp.name || '';
    }
    return '';
  }

  get detailJsonRaw(): any {
    if (!this.row || !this.row.raw) {
      return null;
    }
    if (this.row.type === 'Sanction' && this.row.raw.sanctionEntity) {
      return this.row.raw.sanctionEntity.jsonData;
    }
    if (this.row.type === 'Pep') {
      return this.row.raw.payloadJson;
    }
    if (this.row.type === 'Badpress' && this.row.raw.badPressEntity) {
      return this.row.raw.badPressEntity.jsonData;
    }
    return null;
  }

  get statusLabel(): string {
    if (!this.row) {
      return '';
    }
    if (this.row.matchResolution === null || this.row.matchResolution === undefined) {
      return 'Open';
    }
    const list = this.matchResolutions || [];
    const match = list.find(v => v.id === this.row.matchResolution);
    return match ? match.name : 'Resolved';
  }

  get falseMatchReasonName(): string {
    if (!this.row || this.row.falseMatchReason == null) {
      return '';
    }
    const match = (this.falseMatchReasons || []).find(v => v.id === this.row.falseMatchReason);
    return match ? match.name : '';
  }

  get pepDescription(): string {
    const pep = this.row && this.row.raw && this.row.raw.pepEntity;
    if (!pep) {
      return '';
    }
    if (pep.description !== undefined && pep.description !== null) {
      return pep.description || '';
    }
    return this.loadingDetail ? 'Loading…' : '';
  }

  get listSpecificEntries(): Array<{ key: string; value: string }> {
    const obj = this.parseJson(this.detailJsonRaw);
    if (!obj || typeof obj !== 'object') {
      return [];
    }
    const skip = [
      'sanctionEntityId', 'source', 'matchedNames', 'matchedName', 'highlight',
      // Surfaced in the adverse-media panel when present
      'title', 'url', 'description', 'excerpt', 'provider', 'providerName',
      'relevanceScore', 'sentimentScore', 'categories', 'sourceChannel', 'sourceTier',
      'clusterId', 'disambiguation', 'keywords', 'datePublished', 'market',
    ];
    const entries: Array<{ key: string; value: string }> = [];

    const add = (o: any, prefix: string) => {
      if (!o || typeof o !== 'object') {
        return;
      }
      Object.keys(o).sort().forEach(k => {
        if (prefix === '' && skip.indexOf(k) >= 0) {
          return;
        }
        const v = o[k];
        const key = prefix ? prefix + '.' + k : k;
        if (v == null) {
          return;
        }
        if (Array.isArray(v)) {
          if (v.length === 0) {
            return;
          }
          const value = v.map(x => typeof x === 'object' ? JSON.stringify(x) : String(x)).join(', ').trim();
          if (value) {
            entries.push({ key, value });
          }
          return;
        }
        if (typeof v === 'object' && !(v instanceof Date)) {
          // Flatten disambiguation into labeled rows only when not already shown
          if (prefix === '' && k === 'disambiguation' && this.isAdverseMediaNews) {
            return;
          }
          add(v, key);
          return;
        }
        const value = String(v).trim();
        if (value && value !== '-') {
          entries.push({ key, value });
        }
      });
    };
    add(obj, '');
    return entries;
  }

  get badPressJson(): any {
    return this.parseJson(
      this.row && this.row.raw && this.row.raw.badPressEntity && this.row.raw.badPressEntity.jsonData) || {};
  }

  get hitTypeLabel(): string {
    if (!this.row) {
      return '';
    }
    if (this.row.type === 'Badpress') {
      return this.isAdverseMediaNews ? 'Adverse media' : (this.isLeakPapers ? 'Leak papers' : 'Bad press');
    }
    return this.row.type;
  }

  get isLeakPapers(): boolean {
    const src = this.badPressSourceRaw;
    return !!src && src.indexOf('LEAK') >= 0;
  }

  get isAdverseMediaNews(): boolean {
    const src = this.badPressSourceRaw;
    if (src === 'NEWS') {
      return true;
    }
    // Heuristic: news VO fields or Beamar article record
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    if (bp && (bp.title || bp.url || bp.provider_name)) {
      return true;
    }
    const j = this.badPressJson;
    return !!(j && (j.articleUrl || j.url || j.relevanceScore != null || j.sourceChannel));
  }

  get adverseMediaHeading(): string {
    return this.isAdverseMediaNews ? 'Adverse media' : (this.isLeakPapers ? 'Leak papers' : 'Bad press');
  }

  private get badPressSourceRaw(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    return bp && bp.source ? String(bp.source).toUpperCase() : '';
  }

  private adverseRecord(): any {
    const j = this.badPressJson;
    if (j && j.record && typeof j.record === 'object') {
      return Object.assign({}, j.record, j);
    }
    return j || {};
  }

  get adverseHeadline(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    const fromRecord = (r.record && r.record.title) || '';
    return (bp && bp.title)
      || r.title
      || fromRecord
      || (this.row && this.row.listName)
      || '';
  }

  get adverseArticleUrl(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    return (bp && bp.url) || r.url || r.articleUrl || '';
  }

  get adverseProvider(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    return (bp && bp.provider_name) || r.provider || r.providerName || '';
  }

  get adversePublished(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    const raw = (bp && bp.published_date) || r.publishedAt || r.datePublished || '';
    return raw ? String(raw) : '';
  }

  get adverseSourceChannel(): string {
    const r = this.adverseRecord();
    const ch = r.sourceChannel || '';
    if (!ch) {
      return '';
    }
    return String(ch) === 'gdelt' ? 'GDELT' : (String(ch) === 'bing' ? 'Bing News' : String(ch));
  }

  get adverseCategories(): string {
    const r = this.adverseRecord();
    const cats = r.categories;
    if (Array.isArray(cats) && cats.length) {
      return cats.map((c: any) => String(c).replace(/_/g, ' ')).join(', ');
    }
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    return (bp && bp.category) || r.category || '';
  }

  get adverseRelevance(): string {
    const r = this.adverseRecord();
    if (r.relevanceScore == null || r.relevanceScore === '') {
      return '';
    }
    const n = Number(r.relevanceScore);
    return isFinite(n) ? n.toFixed(2) : String(r.relevanceScore);
  }

  get adverseSentiment(): string {
    const r = this.adverseRecord();
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const score = r.sentimentScore != null ? r.sentimentScore
      : (bp && bp.emotionScore != null ? bp.emotionScore : null);
    if (score == null || score === '') {
      return '';
    }
    const n = Number(score);
    const formatted = isFinite(n) ? n.toFixed(2) : String(score);
    const type = (bp && bp.emotionType) || r.emotionType;
    return type ? `${formatted} (${type})` : formatted;
  }

  get adverseSourceTier(): string {
    const r = this.adverseRecord();
    return r.sourceTier ? String(r.sourceTier) : '';
  }

  get adverseClusterId(): string {
    const r = this.adverseRecord();
    return r.clusterId ? String(r.clusterId) : '';
  }

  get adverseCoEvidence(): string {
    const r = this.adverseRecord();
    const d = r.disambiguation;
    if (!d) {
      return '';
    }
    const parts: string[] = [];
    if (d.mode) {
      parts.push(String(d.mode));
    }
    if (d.reason) {
      parts.push(String(d.reason));
    }
    if (Array.isArray(d.coEvidence) && d.coEvidence.length) {
      parts.push(d.coEvidence.join(', '));
    } else if (Array.isArray(d.matchedTokens) && d.matchedTokens.length) {
      parts.push('tokens: ' + d.matchedTokens.join(', '));
    }
    return parts.join(' · ');
  }

  get adverseDescription(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    return (bp && bp.description) || r.description || '';
  }

  get adverseExcerpt(): string {
    const r = this.adverseRecord();
    return r.excerpt ? String(r.excerpt) : '';
  }

  get adverseKeywords(): string {
    const bp = this.row && this.row.raw && this.row.raw.badPressEntity;
    const r = this.adverseRecord();
    return (bp && bp.keywords) || r.keywords || r.keywordsUsed || '';
  }

  buildResolveForm() {
    this.resolveForm = this.formBuilder.group({
      matchResolutionControl: ['', Validators.required],
      comments: [''],
      falseMatch: ['']
    });
  }

  setUpMatchResolutions(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.matchResolutions = response.values;
        this.cdr.detectChanges();
      });
    } else {
      this.matchResolutions = dropDownValues;
    }
  }

  setUpFalseMatchReasons(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(response => {
        this.warmUpService.setApplicationTypeInLS(response);
        this.falseMatchReasons = response.values;
        this.cdr.detectChanges();
      });
    } else {
      this.falseMatchReasons = dropDownValues;
    }
  }

  selectMatchResolution(event: any) {
    this.selectedMatchResolution = event && event.value != null ? String(event.value) : '';
  }

  isSelectedMatchFalse(): boolean {
    if (!this.matchResolutions || !this.selectedMatchResolution) {
      return false;
    }
    const falseMatch = this.matchResolutions.find(r => r.name === 'False');
    return !!falseMatch && String(falseMatch.id) === String(this.selectedMatchResolution);
  }

  loadDetailIfNeeded() {
    if (!this.row || !this.entityId) {
      return;
    }
    if (this.row.type === 'Sanction') {
      const sanction = this.row.raw && this.row.raw.sanctionEntity;
      if (!sanction || !sanction.id || sanction.jsonData) {
        return;
      }
      this.loadingDetail = true;
      this.sanctionService.getSanctionMatchDetail(this.entityId, sanction.id).subscribe(
        result => {
          if (result && result.sanctionEntity && this.row.raw && this.row.raw.sanctionEntity) {
            this.row.raw.sanctionEntity.jsonData = result.sanctionEntity.jsonData;
            if (result.sanctionEntity.matchedTokens) {
              this.row.hitTokens = result.sanctionEntity.matchedTokens;
            } else {
              this.row.hitTokens = this.extractMatchedTokens(result.sanctionEntity.jsonData) || this.row.hitTokens;
            }
            const full = this.extractListDisplayName(result.sanctionEntity.jsonData);
            if (full) {
              this.row.listName = full;
            }
          }
          this.loadingDetail = false;
          this.cdr.detectChanges();
        },
        () => { this.loadingDetail = false; }
      );
      return;
    }
    if (this.row.type === 'Pep') {
      const pep = this.row.raw && this.row.raw.pepEntity;
      if (!pep || pep.id == null) {
        return;
      }
      if (pep.description !== undefined && pep.description !== null && this.row.raw.payloadJson) {
        return;
      }
      this.loadingDetail = true;
      this.politicallyExposedService.getPepMatchDetail(this.entityId, pep.id).subscribe(
        result => {
          if (result && result.pepEntity && this.row.raw && this.row.raw.pepEntity) {
            this.row.raw.pepEntity.description = result.pepEntity.description;
          }
          if (result && result.payloadJson) {
            this.row.raw.payloadJson = result.payloadJson;
          }
          this.loadingDetail = false;
          this.cdr.detectChanges();
        },
        () => { this.loadingDetail = false; }
      );
      return;
    }
    if (this.row.type === 'Badpress') {
      const bp = this.row.raw && this.row.raw.badPressEntity;
      // List payloads often omit jsonData (heavy); fetch for relevance / excerpt / co-evidence.
      if (!bp || bp.id == null || bp.jsonData) {
        return;
      }
      this.loadingDetail = true;
      this.badPressService.getBadPressMatchDetail(this.entityId, bp.id).subscribe(
        result => {
          if (result && result.badPressEntity && this.row.raw && this.row.raw.badPressEntity) {
            const dest = this.row.raw.badPressEntity;
            const src = result.badPressEntity;
            if (src.jsonData) {
              dest.jsonData = src.jsonData;
            }
            ['title', 'url', 'description', 'provider_name', 'category', 'published_date',
              'keywords', 'emotionScore', 'emotionMagnitude', 'emotionType', 'source'].forEach(k => {
              if (src[k] !== undefined && src[k] !== null) {
                dest[k] = src[k];
              }
            });
            if (src.title) {
              this.row.listName = src.title;
            }
          }
          this.loadingDetail = false;
          this.cdr.detectChanges();
        },
        () => { this.loadingDetail = false; }
      );
    }
  }

  onSubmitResolve({ value, valid }: { value: any; valid: boolean }) {
    if (!valid || !this.isOpen || this.submitting) {
      return;
    }
    this.submitting = true;
    const payload = {
      matchResolution: value.matchResolutionControl,
      falseMatchReason: value.falseMatch,
      comments: value.comments
    };

    let request: Observable<any>;
    if (this.row.type === 'Sanction') {
      const record = new SanctionMatchRecord();
      const entity = new SanctionEntity();
      entity.id = this.row.raw.sanctionEntity.id;
      record.sanctionEntity = entity;
      record.relationEntityId = this.row.relationEntityId;
      record.matchResolution = payload.matchResolution;
      record.falseMatchReason = payload.falseMatchReason;
      record.comments = payload.comments;
      request = this.sanctionService.markSanctionResolve(record);
    } else if (this.row.type === 'Pep') {
      const record = new PepMatchRecord();
      record.pepEntity = this.row.raw.pepEntity;
      record.relationEntityId = this.row.relationEntityId;
      record.matchResolution = payload.matchResolution;
      record.falseMatchReason = payload.falseMatchReason;
      record.comments = payload.comments;
      request = this.politicallyExposedService.markPepResolve(record);
    } else {
      const record = new BadPressMatchRecord();
      record.badPressEntity = this.row.raw.badPressEntity;
      record.relationEntityId = this.row.relationEntityId;
      record.matchResolution = payload.matchResolution;
      record.falseMatchReason = payload.falseMatchReason;
      record.comments = payload.comments;
      request = this.badPressService.markBadPressResolve(record);
    }

    request.subscribe(
      response => {
        this.submitting = false;
        if (response && response.flagResult) {
          this.snackBarService.showStatus(
            response,
            this.row.type === 'Sanction' ? MessageService.SANCTION_RESOLVE_SUCCESS
              : this.row.type === 'Pep' ? MessageService.PEP_RESOLVE_SUCCESS
              : MessageService.BADPRESS_RESOLVE_SUCCESS,
            this.row.type === 'Sanction' ? MessageService.SANCTION_RESOLVE_FAILURE
              : this.row.type === 'Pep' ? MessageService.PEP_RESOLVE_FAILURE
              : MessageService.BADPRESS_RESOLVE_FAILURE);
          this.dialogRef.close({
            resolved: true,
            matchResolution: payload.matchResolution,
            falseMatchReason: payload.falseMatchReason,
            comments: payload.comments
          });
        } else {
          this.snackBarService.showStatus(
            response,
            MessageService.SANCTION_RESOLVE_SUCCESS,
            MessageService.SANCTION_RESOLVE_FAILURE);
        }
      },
      err => {
        this.submitting = false;
        this.snackBarService.showStatus(
          err,
          MessageService.SANCTION_RESOLVE_SUCCESS,
          MessageService.SANCTION_RESOLVE_FAILURE);
      }
    );
  }

  close() {
    this.dialogRef.close();
  }

  private parseJson(raw: any): any {
    if (!raw) {
      return null;
    }
    try {
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (_e) {
      return null;
    }
  }

  private extractListDisplayName(jsonRaw: any): string {
    const obj = this.parseJson(jsonRaw);
    if (!obj) {
      return '';
    }
    if (obj.matchedName && String(obj.matchedName).trim()) {
      return String(obj.matchedName).trim();
    }
    const names = (obj.record && obj.record.names) || obj.names;
    if (Array.isArray(names)) {
      for (let i = 0; i < names.length; i++) {
        if (names[i] && String(names[i]).trim()) {
          return String(names[i]).trim();
        }
      }
    }
    const single = (obj.record && (obj.record.name || obj.record.fullName || obj.record.full_name))
      || obj.name || obj.fullName || obj.full_name;
    return single && String(single).trim() ? String(single).trim() : '';
  }

  private extractMatchedTokens(jsonRaw: any): string {
    const obj = this.parseJson(jsonRaw);
    if (!obj || !Array.isArray(obj.matchedNames)) {
      return '';
    }
    return obj.matchedNames
      .map((x: any) => x == null ? '' : String(x).trim())
      .filter((s: string) => !!s)
      .join(' / ');
  }
}



// WEBPACK FOOTER //
// ./src/app/entities/monitoring/dashboard/hit-detail-dialog/hit-detail-dialog.component.ts